/**
 * RUM (Real User Monitoring) — captures Core Web Vitals + slow resources
 * and sends batched beacons to the log-performance edge function.
 *
 * Admin-only data; never affects user-facing UX.
 */
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from "web-vitals";

type Beacon = {
  metric_name: string;
  value: number;
  rating?: string;
  route: string;
  path: string;
  device_type: string;
  connection_type?: string;
  context?: Record<string, unknown>;
  session_id: string;
};

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-performance`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

let queue: Beacon[] = [];
let flushTimer: number | null = null;
let initialized = false;

const SESSION_ID =
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `s_${Math.random().toString(36).slice(2)}_${Date.now()}`;

const ROUTE_PATTERNS: Array<[RegExp, string]> = [
  [/^\/$/, "/"],
  [/^\/today$/, "/today"],
  [/^\/search$/, "/search"],
  [/^\/login$/, "/login"],
  [/^\/forgot-password$/, "/forgot-password"],
  [/^\/reset-password$/, "/reset-password"],
  [/^\/saved$/, "/saved"],
  [/^\/profile$/, "/profile"],
  [/^\/admin/, "/admin"],
  [/^\/unsubscribe$/, "/unsubscribe"],
  [/^\/episode\/[^/]+$/, "/episode/:slug"],
  [/^\/show\/[^/]+$/, "/show/:slug"],
  [/^\/topic\/[^/]+$/, "/topic/:slug"],
  [/^\/answer\/[^/]+$/, "/answer/:slug"],
  [/^\/answers$/, "/answers"],
  [/^\/list\/[^/]+$/, "/list/:slug"],
];

export function getRoutePattern(pathname: string): string {
  for (const [re, label] of ROUTE_PATTERNS) {
    if (re.test(pathname)) return label;
  }
  return "/other";
}

function getDeviceType(): string {
  if (typeof window === "undefined") return "unknown";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function getConnectionType(): string | undefined {
  const c = (navigator as any).connection;
  return c?.effectiveType;
}

function flush(useBeacon = false) {
  if (queue.length === 0) return;
  const payload = JSON.stringify({ metrics: queue });
  queue = [];
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  // Skip silently if env not configured
  if (!ENDPOINT || !ANON_KEY) return;

  if (useBeacon && navigator.sendBeacon) {
    // sendBeacon doesn't let us add Authorization header — append apikey as query param
    const url = `${ENDPOINT}?apikey=${ANON_KEY}`;
    try {
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
    } catch {
      /* ignore */
    }
    return;
  }

  fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: ANON_KEY,
      Authorization: `Bearer ${ANON_KEY}`,
    },
    body: payload,
    keepalive: true,
  }).catch(() => {
    /* ignore — telemetry must never throw */
  });
}

function scheduleFlush() {
  if (flushTimer !== null) return;
  flushTimer = window.setTimeout(() => flush(false), 5000);
}

export function recordMetric(b: Omit<Beacon, "session_id" | "route" | "path" | "device_type">) {
  if (typeof window === "undefined") return;
  queue.push({
    ...b,
    route: getRoutePattern(window.location.pathname),
    path: window.location.pathname,
    device_type: getDeviceType(),
    connection_type: b.connection_type ?? getConnectionType(),
    session_id: SESSION_ID,
  });
  scheduleFlush();
}

function handleVital(metric: Metric) {
  recordMetric({
    metric_name: metric.name,
    value: metric.value,
    rating: metric.rating,
    context: { delta: metric.delta, navigationType: metric.navigationType },
  });
}

function observeSlowResources() {
  if (typeof PerformanceObserver === "undefined") return;
  try {
    const po = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const e = entry as PerformanceResourceTiming;
        if (e.duration < 1500) continue; // only slow ones
        // Trim long URLs to host + path
        let name = e.name;
        try {
          const u = new URL(name);
          name = u.host + u.pathname.slice(0, 80);
        } catch {
          name = name.slice(0, 120);
        }
        recordMetric({
          metric_name: "resource_slow",
          value: Math.round(e.duration),
          context: {
            name,
            initiator: e.initiatorType,
            transferSize: e.transferSize,
          },
        });
      }
    });
    po.observe({ type: "resource", buffered: true });
  } catch {
    /* ignore */
  }
}

function trimUrl(u: string): string {
  try {
    const url = new URL(u, window.location.origin);
    // Strip query/hash, keep host + first 80 chars of path
    return (url.host + url.pathname).slice(0, 200);
  } catch {
    return u.slice(0, 200);
  }
}

function trimMessage(m: unknown): string {
  if (typeof m !== "string") {
    try {
      m = String(m);
    } catch {
      m = "(unstringifiable)";
    }
  }
  return (m as string).slice(0, 240);
}

function observeErrors() {
  if (typeof window === "undefined") return;

  // Uncaught JS exceptions
  window.addEventListener("error", (event) => {
    if (!event?.message) return;
    recordMetric({
      metric_name: "js_error",
      value: 1,
      context: {
        message: trimMessage(event.message),
        filename: event.filename ? trimUrl(event.filename) : undefined,
        lineno: event.lineno,
        colno: event.colno,
        name: (event.error as Error | undefined)?.name,
      },
    });
  });

  // Unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error ? reason.message : trimMessage(reason);
    recordMetric({
      metric_name: "js_error",
      value: 1,
      context: {
        message,
        name: reason instanceof Error ? reason.name : "UnhandledRejection",
      },
    });
  });
}

function instrumentFetch() {
  if (typeof window === "undefined" || !window.fetch) return;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const [input, init] = args;
    const method =
      (init?.method ||
        (typeof input !== "string" && "method" in (input as Request)
          ? (input as Request).method
          : "GET")) ?? "GET";
    let url: string;
    try {
      url =
        typeof input === "string"
          ? input
          : input instanceof URL
          ? input.toString()
          : (input as Request).url;
    } catch {
      url = "(unknown)";
    }

    // Skip our own telemetry endpoint to avoid feedback loops
    if (url.includes("/log-performance")) {
      return originalFetch(...args);
    }

    const start = performance.now();
    try {
      const res = await originalFetch(...args);
      // Track 4xx/5xx as http_error; value = status code
      if (res.status >= 400) {
        recordMetric({
          metric_name: "http_error",
          value: res.status,
          context: {
            url: trimUrl(url),
            method,
            duration_ms: Math.round(performance.now() - start),
          },
        });
      }
      return res;
    } catch (err) {
      // Network failure (CORS, offline, DNS, abort, etc.)
      recordMetric({
        metric_name: "api_failure",
        value: 0,
        context: {
          url: trimUrl(url),
          method,
          message: trimMessage((err as Error)?.message ?? "fetch failed"),
          duration_ms: Math.round(performance.now() - start),
        },
      });
      throw err;
    }
  };
}

export function initRUM() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  onCLS(handleVital);
  onFCP(handleVital);
  onINP(handleVital);
  onLCP(handleVital);
  onTTFB(handleVital);

  observeSlowResources();
  observeErrors();
  instrumentFetch();

  // Flush on page hide / unload
  const flushBeacon = () => flush(true);
  window.addEventListener("pagehide", flushBeacon);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushBeacon();
  });
}
